import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ValueMappingsTable from '@Components/ValueMappingsTable';
import {
	changeValueMappingTableProfilesFilter,
	changeValueMappingTableProfilesPageSize,
	changeValueMappingTableProfilesSort,
	getValueMappingProfiles,
} from '@Actions/valueMapping';
import { getValue } from '@Selectors';
import {
	GetValuemappingProfilesRequest,
	ROWS_PER_PAGE,
	ValuemappingManagerStoreState,
} from '@Types';
import { useValueMappingProfilesPagination } from '@Hooks';

const ValueMapping = () => {
	const {
		valueMappingProfiles: {
			initialized,
			valueMappingProfiles: { list, filters, tableState, pagination },
		},
	} = useSelector(getValue);
	const dispatch = useDispatch();

	const { createSearchParams } = useValueMappingProfilesPagination();

	useEffect(() => {
		fetchValueMappingProfilesHandler({ page: pagination.current });
	}, []);

	const fetchValueMappingProfilesHandler = (options: {
		page?: number;
		pageSize?: number;
		sort?: GetValuemappingProfilesRequest['sort'];
		search?: string;
		filters?: ValuemappingManagerStoreState['valueMappingProfiles']['filters'];
	}): void => {
		const searchParams = createSearchParams({
			page: options.page || 0,
			pageSize: options.pageSize || ROWS_PER_PAGE[tableState.limitIndex],
			sort: options.sort || tableState.sort,
			search: options.search ?? tableState.searchQuery,
			filters: options.filters || filters,
		});
		dispatch(getValueMappingProfiles(searchParams));
	};

	const handleFetchValueMappingProfiles = (page: number): void => {
		fetchValueMappingProfilesHandler({ page: page });
	};

	const handleChangeValueMappingProfilesPageSize = (
		size: number,
		pageSizeOptionsIndex: number,
	): void => {
		dispatch(changeValueMappingTableProfilesPageSize(pageSizeOptionsIndex));
		fetchValueMappingProfilesHandler({
			pageSize: size,
			sort: tableState.sort,
		});
	};

	const handleChangeSort = (
		_event: React.MouseEvent<unknown>,
		name: string | number,
		order: 'asc' | 'desc',
	): void => {
		const sort: GetValuemappingProfilesRequest['sort'] = {
			field: `${name}`,
			sortOrder: order,
		};
		dispatch(changeValueMappingTableProfilesSort(sort));
		fetchValueMappingProfilesHandler({ sort: sort });
	};

	const handleChangeSearch = (query: string): void => {
		fetchValueMappingProfilesHandler({ search: query });
	};

	const handleFilterChange = (name: string, value: string): void => {
		dispatch(changeValueMappingTableProfilesFilter({ name, value }));
		fetchValueMappingProfilesHandler({
			filters: {
				...filters,
				[name]: { ...filters[name], selected: value },
			},
		});
	};

	return (
		<>
			<ValueMappingsTable
				initialized={initialized}
				list={list}
				tableState={tableState}
				pagination={pagination}
				filters={filters}
				fetchValueMappingProfiles={handleFetchValueMappingProfiles}
				onChangeValueMappingProfilesPageSize={
					handleChangeValueMappingProfilesPageSize
				}
				onChangeSort={handleChangeSort}
				onChangeSearch={handleChangeSearch}
				filterChangedHandler={handleFilterChange}
			/>
		</>
	);
};

export default ValueMapping;
